import { Card } from '@components/common/Card/Card';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  useDeleteRoleMutation,
  useGetPermissionsQuery,
  useGetRoleByNameQuery,
  useGetRolePermissionsQuery,
  useUpdateRoleMutation,
} from '@/api/roles/api';
import { PermissionResponse, RoleRequest } from '@/api/roles/types';
import {
  permissionsTableColumns,
  roleFormSchema,
} from '@/pages/Administration/components/roles/card/formSchema.config';
import { createModalConfig } from '@/pages/Administration/components/roles/modal.config';
import { RoleType } from '@/shared/roles/enums';
import { administration } from '@/shared/security/permissions/administration';
import { groupByModule, mapTableIndexWithPage } from '@/utils/utils';

export const RoleCard = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { name } = useParams();
  const navigate = useNavigate();

  const { data: record, isLoading, isFetching } = useGetRoleByNameQuery(name as string);
  const {
    data: rolePermissions,
    isLoading: isRolePermissionsLoading,
    isFetching: isRolePermissionsFetching,
  } = useGetRolePermissionsQuery(name as string);

  const [deleteRole, { isLoading: isDeleting, isSuccess: isDeleteSuccess }] =
    useDeleteRoleMutation();

  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  const { data: allPermissions, isLoading: isPermissionsLoading } =
    useGetPermissionsQuery({});

  const handleEdit = async (values: RoleRequest) => {
    try {
      await updateRole({ roleName: name as string, ...values }).unwrap();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Ошибка при обновлении роли:', error);
    }
  };
  const handleDelete = async () => {
    await deleteRole(name as string);
  };

  useEffect(() => {
    if (isDeleteSuccess) {
      setIsDeleteModalOpen(false);
      navigate(-1);
    }
  }, [isDeleteSuccess]);

  return (
    <>
      <Modal
        mode={'confirm'}
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onSubmit={handleDelete}
        confirmLoading={isDeleting}
        text={'Вы уверены, что хотите удалить выбранные данные?'}
        submitText={'Да'}
        cancelText={'Нет'}
      />

      <Modal
        mode={'form'}
        open={isEditModalOpen}
        title={'Редактировать роль'}
        schema={createModalConfig(groupByModule(allPermissions as PermissionResponse[]))}
        data={{ ...record, permissions: rolePermissions?.map((item: any) => item?.name) }}
        formKey={'editUser'}
        destroyOnHidden={true}
        onSubmit={handleEdit}
        onCancel={() => setIsEditModalOpen(false)}
        confirmLoading={isUpdating}
        buttons={{
          submit: { text: 'Сохранить', type: 'primary' },
          cancel: { text: 'Отмена', type: 'default' },
        }}
      />
      <PageTitle title={'Общие сведения о роли'} />
      <Card
        viewMode
        loading={isLoading || isFetching}
        schema={roleFormSchema}
        data={record}
        actions={{
          onBack: () => navigate(-1),
          ...(record?.type === RoleType.USER && {
            onEdit: {
              action: () => setIsEditModalOpen(true),
              security: {
                permissions: administration.roles.edit,
              },
            },
          }),
          extra: [
            ...(record?.type === RoleType.USER
              ? [
                  {
                    text: 'Удалить',
                    action: () => setIsDeleteModalOpen(true),
                    security: {
                      permissions: administration.roles.delete,
                    },
                  },
                ]
              : []),
          ],
        }}
      />
      <UTable
        style={{ marginTop: 50 }}
        columns={permissionsTableColumns}
        data={mapTableIndexWithPage(rolePermissions)}
        loading={isRolePermissionsLoading || isRolePermissionsFetching}
      />
    </>
  );
};
